import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Link } from "react-router";
import { ExternalLink, Loader2, Mic, Music, Trash2 } from "lucide-react";
import { BouquetIcon } from "../misc/BouquetIcon";
import { AudioPill } from "../misc/AudioPill";

import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";

// LDF: Auswertung aller Blumengrüße – Website-Karten und Grüße aus dem CRM, rückwirkend seit Start.

type Row = {
  card_id: string;
  created_at: string;
  source: "website" | "crm-luftpost" | "crm-klassisch";
  sender: string | null;
  recipient: string | null;
  emails: string[];
  contact_id: number | null;
  contact_name: string | null;
  channel: string | null;
  whatsapp: boolean | null;
  opened_count: number;
  first_opened_at: string | null;
  last_opened_at: string | null;
  recipient_opens: number;
  team_opens: number;
  opens_mail: number;
  opens_whatsapp: number;
  opens_link: number;
  opens_handy: number;
  opens_computer: number;
  opens_tablet: number;
  made_count: number;
  referred_by: string | null;
  has_voice: boolean;
  has_music: boolean;
  short_code: string | null;
  voice_path: string | null;
  music_title: string | null;
  music_artist: string | null;
  music_link: string | null;
  music_preview: string | null;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

const SOURCES: Record<Row["source"], { label: string; className: string }> = {
  website: { label: "Website", className: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200" },
  "crm-luftpost": { label: "CRM · Luftpost", className: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  "crm-klassisch": { label: "CRM · klassisch", className: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
};

const dt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "–";

const cardLink = (r: Row) =>
  `${r.short_code ? `https://lovelydayflowers.de/k/${r.short_code}` : `https://lovelydayflowers.de/karte?id=${r.card_id}`}${r.short_code ? "?" : "&"}team=1`;

export const GreetingsReportPage = () => {
  const [source, setSource] = useState<"" | Row["source"] | "crm">("");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const remove = async (r: Row) => {
    const who = [r.sender, r.recipient].filter(Boolean).join(" → ") || "diese Karte";
    if (!window.confirm(`Grußkarte „${who}“ endgültig löschen?\n\nDer Link funktioniert danach nicht mehr, Sprachnachricht und Aufrufe werden mitgelöscht.`)) return;
    setDeleting(r.card_id);
    const { error } = await getSupabaseClient().functions.invoke("greeting", { method: "POST", body: { action: "delete_card", card_id: r.card_id } });
    setDeleting(null);
    if (error) return notify("Löschen fehlgeschlagen", { type: "error" });
    notify("Grußkarte gelöscht", { type: "success" });
    queryClient.invalidateQueries({ queryKey: ["greeting-report"] });
    queryClient.invalidateQueries({ queryKey: ["greetings"] });
  };

  const { data, isPending, error } = useQuery({
    queryKey: ["greeting-report"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("greeting_report");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? []).filter(
      (r) =>
        (!source || (source === "crm" ? r.source !== "website" : r.source === source)) &&
        (!term || [r.sender, r.recipient, r.contact_name, ...r.emails].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [data, source, search]);

  const stats = useMemo(() => {
    const all = rows;
    // Empfänger-Öffnungen erst seit Einführung des Zugriffsverlaufs genau – davor zählt der alte Zähler
    const opened = all.filter((r) => r.recipient_opens > 0 || r.opened_count > 0).length;
    return {
      total: all.length,
      website: all.filter((r) => r.source === "website").length,
      crm: all.filter((r) => r.source !== "website").length,
      withEmail: all.filter((r) => r.emails.length).length,
      opened,
      made: all.reduce((n, r) => n + r.made_count, 0),
      viaChain: all.filter((r) => r.referred_by).length,
      handy: all.reduce((n, r) => n + r.opens_handy, 0),
      computer: all.reduce((n, r) => n + r.opens_computer + r.opens_tablet, 0),
    };
  }, [rows]);

  return (
    <div className="mx-auto mt-4 max-w-[1800px]">
      <Card>
        <CardContent className="space-y-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-semibold">
              <BouquetIcon className="h-5 w-5" /> Blumengrüße – Auswertung
            </h1>
            <p className="text-sm text-muted-foreground">
              Alle digitalen Grußkarten seit Start: von der Website und aus dem CRM. „Geöffnet“ zählt nur Empfänger (eigene Aufrufe über das CRM
              nicht); Versandweg und Gerät gibt es seit dem 15.09.2026.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label="Grußkarten" value={stats.total} />
            <Tile label="Website" value={stats.website} />
            <Tile label="CRM" value={stats.crm} />
            <Tile label="Geöffnet" value={`${stats.opened}`} hint={stats.total ? `${Math.round((stats.opened / stats.total) * 100)} %` : undefined} />
            <Tile label="„Eigenen Blumengruß“ geklickt" value={stats.made} hint={`${stats.viaChain} Karten weitergeschenkt`} />
            <Tile label="Handy / Computer" value={`${stats.handy} / ${stats.computer}`} hint="Aufrufe seit 15.09." />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-full bg-muted p-1">
              {(
                [
                  ["", "Alle"],
                  ["website", "Website"],
                  ["crm", "CRM"],
                  ["crm-luftpost", "Luftpost"],
                  ["crm-klassisch", "Klassisch"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSource(value)}
                  className={`rounded-full px-3 py-1 text-sm cursor-pointer ${source === value ? "bg-background font-medium shadow-sm" : "text-muted-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen: Absender, Empfänger, E-Mail …"
              className="h-9 min-w-48 flex-1 rounded-md border border-input bg-background px-3 text-sm md:w-80 md:flex-none"
            />
            <span className="text-xs text-muted-foreground">{rows.length} Karten</span>
          </div>

          {isPending ? (
            <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
          ) : error ? (
            <p className="py-6 text-sm text-destructive">Auswertung konnte nicht geladen werden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1280px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pl-2 pr-3 font-medium">Erstellt</th>
                    <th className="py-2 pr-3 font-medium">Quelle</th>
                    <th className="py-2 pr-3 font-medium">Von</th>
                    <th className="py-2 pr-3 font-medium">An</th>
                    <th className="py-2 pr-3 font-medium">Ziel-E-Mail</th>
                    <th className="py-2 pr-3 font-medium">Versand</th>
                    <th className="py-2 pr-3 font-medium">Sprachnachricht</th>
                    <th className="py-2 pr-3 font-medium">Musik</th>
                    <th className="py-2 pr-3 text-right font-medium">Geöffnet</th>
                    <th className="py-2 pr-3 font-medium">Zuletzt</th>
                    <th className="py-2 pr-3 font-medium">Weg / Gerät</th>
                    <th className="py-2 pr-3 text-right font-medium">Eigener Blumengruß</th>
                    <th className="py-2 pr-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const opens = r.recipient_opens || r.opened_count;
                    const ways = [
                      r.opens_mail && `E-Mail ${r.opens_mail}`,
                      r.opens_whatsapp && `WhatsApp ${r.opens_whatsapp}`,
                      r.opens_link && `Link ${r.opens_link}`,
                    ].filter(Boolean);
                    const devices = [
                      r.opens_handy && `📱 ${r.opens_handy}`,
                      r.opens_tablet && `Tablet ${r.opens_tablet}`,
                      r.opens_computer && `💻 ${r.opens_computer}`,
                    ].filter(Boolean);
                    return (
                      <tr key={r.card_id} className="border-b border-border align-top hover:bg-muted/50">
                        <td className="whitespace-nowrap py-2 pl-2 pr-3 text-muted-foreground">{dt(r.created_at)}</td>
                        <td className="py-2 pr-3">
                          <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${SOURCES[r.source].className}`}>{SOURCES[r.source].label}</span>
                        </td>
                        <td className="py-2 pr-3">{r.sender || <span className="text-muted-foreground">–</span>}</td>
                        <td className="py-2 pr-3">
                          {r.contact_id ? (
                            <Link to={`/contacts/${r.contact_id}/show`}>{r.contact_name || r.recipient}</Link>
                          ) : (
                            r.recipient || <span className="text-muted-foreground">–</span>
                          )}
                        </td>
                        <td className="max-w-56 py-2 pr-3">
                          {r.emails.length ? (
                            r.emails.map((e) => (
                              <div key={e} className="truncate" title={e}>
                                {e}
                              </div>
                            ))
                          ) : (
                            <span className="text-muted-foreground">–</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-xs text-muted-foreground">
                          {r.source === "website"
                            ? r.emails.length
                              ? "E-Mail"
                              : "Link geteilt"
                            : r.channel === "email"
                              ? r.whatsapp
                                ? "E-Mail + WhatsApp"
                                : "E-Mail"
                              : r.channel === "whatsapp"
                                ? "WhatsApp"
                                : "Link"}
                        </td>
                        <td className="py-2 pr-3">
                          {r.voice_path ? (
                            <div className="flex items-center gap-1">
                              <Mic className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Sprachnachricht" />
                              <AudioPill src={`${SUPABASE_URL}/storage/v1/object/public/gallery/${r.voice_path}`} className="w-36" />
                            </div>
                          ) : (
                            <span className="text-muted-foreground">–</span>
                          )}
                        </td>
                        <td className="max-w-52 py-2 pr-3 text-xs">
                          {r.music_title || r.music_link ? (
                            <div className="flex items-start gap-1">
                              <Music className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Musik" />
                              <div className="min-w-0">
                                {r.music_title ? (
                                  <>
                                    <div className="truncate font-medium" title={r.music_title}>{r.music_title}</div>
                                    <div className="truncate text-muted-foreground">{r.music_artist}</div>
                                    {/* 30-Sekunden-Vorschau wie auf der Karte */}
                                    {r.music_preview && <AudioPill src={r.music_preview} className="mt-1 w-36" />}
                                  </>
                                ) : (
                                  <a href={r.music_link!} target="_blank" rel="noopener noreferrer" className="underline">
                                    Song-Link
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">–</span>
                          )}
                        </td>
                        <td className={`py-2 pr-3 text-right ${opens ? "font-medium text-green-700 dark:text-green-400" : "text-muted-foreground"}`}>
                          {opens ? `${opens}×` : "–"}
                          {r.team_opens > 0 && <div className="text-[10px] font-normal text-muted-foreground">+{r.team_opens} Team</div>}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">{dt(r.last_opened_at)}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {ways.length || devices.length ? [...ways, ...devices].join(" · ") : "–"}
                        </td>
                        <td className="py-2 pr-3 text-right">{r.made_count ? `${r.made_count}×` : <span className="text-muted-foreground">–</span>}</td>
                        <td className="py-2 pr-2">
                          <div className="flex items-center gap-2">
                            <a href={cardLink(r)} target="_blank" rel="noopener noreferrer" title="Karte ansehen (zählt als Team)">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                            <button
                              type="button"
                              onClick={() => remove(r)}
                              disabled={deleting === r.card_id}
                              title="Grußkarte löschen"
                              aria-label="Grußkarte löschen"
                              className="text-muted-foreground hover:text-destructive cursor-pointer disabled:opacity-50"
                            >
                              {deleting === r.card_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

GreetingsReportPage.path = "/blumengruesse";

const Tile = ({ label, value, hint }: { label: string; value: number | string; hint?: string }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-xl font-semibold">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);
